import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import { Button, Collapse, Typography } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import ReactGA from 'react-ga4';
import AxuminNavBar from '../../../micro/BrandNavBar/AxuminNavBar';
import TrainingNavBar from '../../../micro/TrainingNavBar/AxuminTrainingNavBar';
import { BASE_URL, DOCUMENT_PATHS, IMAGE_PATHS } from '../../../../constants/paths.constants';
import { darkGrey, charcoalGrey, yellow, white, disabledGrey, dividerGrey, buttonText } from '../../../../app/theme';
import Indication from '../../../micro/Indication/AxuminIndication';
import TrainingFooter from '../../../micro/TrainingFooter/TrainingFooter';
import { TRAINING_NAVIGATION, BASE_ROUTE } from '../../../../constants/trainingNav.constants';
import history from '../../../../services/browserHistory';
import AppState from '../../../../models/redux/state/app.state';
import { getTrainingAPIcall } from '../../../../redux/actions/training/training.actions';
import SingleTraining from '../../../../models/training/SingleTraining';

const useStyles = makeStyles(() => ({
  physicianImage: {
    display: 'block',
    width: '100%',
    maxWidth: '100vw',
    height: 'auto',
  },
  grayBox: {
    backgroundColor: darkGrey,
    width: '100%',
    margin: 0,
    paddingBottom: 56,
  },
  h1: {
    padding: '37px 0 0 64px',
    margin: 0,
  },
  subheader: {
    padding: '10px 0 0 64px',
    margin: 0,
    fontSize: 20,
  },
  charcoalGreyBox: {
    backgroundColor: charcoalGrey,
    width: 'calc(100% - 128px)',
    boxSizing: 'border-box',
    margin: '56px 64px 0',
    paddingTop: 21,
    paddingBottom: 40,
    paddingLeft: 43,
    paddingRight: 31,
  },
  headerRow: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  titleText: {
    fontWeight: 900,
    color: white,
    fontSize: 22,
    marginLeft: -25,
    cursor: 'pointer',
    '&::before': {
      content: '"+"',
      width: '1em',
      display: 'inline-block',
      color: yellow,
      fontWeight: 600,
    },
  },
  expanded: {
    '&::before': {
      content: '"-"',
    },
  },
  disabledText: {
    color: disabledGrey,
    cursor: 'default',
    '&::before': {
      color: disabledGrey,
    },
  },
  enterButton: {
    width: 174,
    fontSize: 16,
    fontWeight: 600,
    borderRadius: 24,
    color: buttonText,
    '&.Mui-disabled': {
      backgroundColor: dividerGrey,
    },
  },
  subHeaderRow: {
    marginTop: 8,
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
  },
  subHeaderItem: {
    width: '25%',
    color: white,
    fontSize: 18,
  },
  h2: {
    fontSize: 20,
    color: white,
    fontWeight: 900,
    marginTop: 28,
    marginBottom: 10,
  },
  bodyText: {
    color: white,
    fontSize: 18,
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    color: white,
    fontSize: 18,
    lineHeight: 1.3,
    '& > li': {
      padding: '8px 0',
      display: 'flex',
      flexDirection: 'row',
    },
    '& > li::before': {
      content: '"\u2022"',
      paddingRight: 15,
      display: 'block',
      color: white,
      fontWeight: 600,
    },
  },
}));

const AxuminTrainingOverview = (): JSX.Element => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { location } = history;
  const [isTrainingExpanded, setIsTrainingExpanded] = useState(true);
  const [isCaseStudiesExpanded, setIsCaseStudiesExpanded] = useState(false);
  const [isLCExpanded, setIsLCExpanded] = useState(false);
  const [isStatementExpanded, setIsStatementExpanded] = useState(false);
  const trainingInfo = useSelector((state: AppState) => state.training.current);
  const [currentTraining, setCurrentTraining] = useState<SingleTraining>();
  const [module, setModule] = useState('');
  const [displayModule, setDisplayModule] = useState('');

  useEffect(() => {
    const segments = location.pathname.split('/');
    const newModule = segments[segments.length - 1];
    const newDisplayModule = newModule[0].toUpperCase() + newModule.substring(1);

    setModule(`axumin${newDisplayModule}`);
    setDisplayModule(newDisplayModule);
    document.title = `Welcome to Axumin ${newDisplayModule} Training`;
    setIsTrainingExpanded(false);
    setIsCaseStudiesExpanded(false);
    setIsLCExpanded(false);
    setIsStatementExpanded(false);
  }, [location]);

  useEffect(() => {
    dispatch(getTrainingAPIcall('1'));
  }, [dispatch]);

  useEffect(() => {
    if (trainingInfo && module) {
      const match = trainingInfo.trainings.find((training) => {
        // These IDs (1 and 2) should probably be saved in a constants file
        // Even better would be to make the API call to get it?
        const idMatch = module === 'axuminInterpretation' ? 1 : 2;
        return idMatch === training.training.id;
      });
      if (!match && currentTraining) {
        history.push('/trainings');
      }
      setCurrentTraining(match);
    }
  }, [trainingInfo, module, currentTraining]);

  const handleClickEnter = () => {
    let baseRoute;
    let navigationData;
    if (module === 'axuminInterpretation') {
      baseRoute = BASE_ROUTE.axuminInterpretation;
      navigationData = TRAINING_NAVIGATION.axuminInterpretation;
    } else {
      baseRoute = BASE_ROUTE.axuminAcquisition;
      navigationData = TRAINING_NAVIGATION.axuminAcquisition;
    }
    const page = currentTraining?.currentPage && !currentTraining?.dateCompleted ? currentTraining.currentPage : 1;
    const { route } = navigationData[page];
    history.push(`${baseRoute}${route}`);
  };
  const caseStudiesExpandHandler = () => {
    if (currentTraining && currentTraining.dateCompleted) {
      setIsCaseStudiesExpanded(!isCaseStudiesExpanded);
    }
  };
  const LCExpandHandler = () => {
    if (currentTraining && currentTraining.dateCompleted) {
      setIsLCExpanded(!isLCExpanded);
    }
  };
  const statementExpandHandler = () => {
    if (currentTraining && currentTraining.dateCompleted) {
      setIsStatementExpanded(!isStatementExpanded);
    }
  };
  const handleClickDownload = () => {
    const action = module === 'axuminInterpretation' ? 'INT Stmt of Completion' : 'ACQ Stmt of Completion';
    ReactGA.event({
      category: 'event',
      action,
    });
  };

  return (
    <>
      <TrainingNavBar />
      <AxuminNavBar section={`Image ${displayModule}`} />
      <img
        className={classes.physicianImage}
        src={`${BASE_URL}${
          module === 'axuminInterpretation'
            ? IMAGE_PATHS.TRAINING_OVERVIEW[1].PHYSICIANS.path
            : IMAGE_PATHS.TRAINING_OVERVIEW[1].TECHNOLOGISTS.path
        }`}
        alt={`${
          module === 'axuminInterpretation'
            ? IMAGE_PATHS.TRAINING_OVERVIEW[1].PHYSICIANS.alt
            : IMAGE_PATHS.TRAINING_OVERVIEW[1].TECHNOLOGISTS.alt
        }`}
      />
      <div className={classes.grayBox}>
        <Typography variant="h1" className={classes.h1}>
          {`Image ${displayModule} Training`}
        </Typography>
        <Typography className={classes.subheader} variant="h6">
          {`Upon completion of the initial required Image ${displayModule} Training, 
          clinical case studies will be available.`}
        </Typography>
        <div className={classes.charcoalGreyBox}>
          <div className={classes.headerRow}>
            <Typography
              className={classNames(classes.titleText, isTrainingExpanded ? classes.expanded : '')}
              onClick={() => setIsTrainingExpanded(!isTrainingExpanded)}
            >
              Required Training
            </Typography>
            <Button variant="contained" color="primary" className={classes.enterButton} onClick={handleClickEnter}>
              Enter
            </Button>
          </div>
          <Collapse in={isTrainingExpanded} timeout="auto" unmountOnExit>
            <div className={classes.subHeaderRow}>
              <span className={classes.subHeaderItem}>
                <b>Time to Complete: </b>
                90 minutes
              </span>
              <span className={classes.subHeaderItem}>
                <b>Date Added: </b>
                {module === 'axuminInterpretation' ? 'January 31, 2022' : 'May 6, 2022'}
              </span>
              <span className={classes.subHeaderItem}>
                <b>Progress: </b>
                {currentTraining && currentTraining.status.name}
              </span>
            </div>
            <Typography className={classes.h2}>Description:</Typography>
            <Typography className={classes.bodyText}>
              {module === 'axuminInterpretation'
                ? 'This training is designed to help physicians understand the image acquisition and interpretation criteria for Axumin'
                : 'This training is designed to help technologists understand the process of dosing and administration of Axumin and optimize image acquisition protocols.'}
            </Typography>
            <Typography className={classes.h2}>Learning Objectives:</Typography>
            <ul className={classes.list}>
              <li>
                Describe the mechanism of action and biodistribution of Axumin for diagnostic imaging in patients with
                suspected prostate cancer recurrence following previous treatment
              </li>
              <li>
                Outline dosing, administration, and patient preparation prior to positron emission tomography/computed
                tomography (PET/CT)
              </li>
              <li>
                Prior to utilizing Axumin for patients with suspected recurrent prostate cancer, identify the most
                effective image acquisition and optimal quality imaging display for better interpretation and
                localization of prostate cancer lesions
              </li>
              {module === 'axuminInterpretation' && (
                <>
                  <li>
                    Understand the appropriate Interpretation criteria based on identifying the location and size of
                    lesions, while reviewing applicable image reconstruction planes
                  </li>
                  <li>
                    Describe the expected physiologic uptake mechanism, incidental findings, and normal variants with
                    Axumin
                  </li>
                </>
              )}
              <li>
                Identify the role of Axumin PET/CT imaging by reviewing clinical case studies of suspected recurrent
                prostate cancer
              </li>
            </ul>
          </Collapse>
        </div>
        <div className={classes.charcoalGreyBox}>
          <div className={classes.headerRow}>
            <Typography
              className={classNames(
                classes.titleText,
                currentTraining && currentTraining.dateCompleted ? ' ' : classes.disabledText,
                isCaseStudiesExpanded ? classes.expanded : '',
              )}
              onClick={caseStudiesExpandHandler}
            >
              Additional Cases
            </Typography>
            <Button
              variant="contained"
              disabled={!currentTraining || !currentTraining.dateCompleted}
              color="primary"
              className={classes.enterButton}
              href="/axumin/casestudies"
            >
              Enter
            </Button>
          </div>
          <Collapse in={isCaseStudiesExpanded} timeout="auto" unmountOnExit>
            <Typography className={classes.h2}>Description:</Typography>
            <Typography className={classes.bodyText}>
              {`This section houses real ${
                module === 'axuminInterpretation' ? 'patient' : 'clinical'
              } cases that provide example images and information available following
              Axumin PET/CT imaging. The additional case studies will give nuclear medicine ${
                module === 'axuminInterpretation' ? 'physicians' : 'technologists'
              } a better
              understanding of their responsibility for delivering accurate and timely image
              acquisition${module === 'axuminInterpretation' ? ' and interpretation' : ''} of images using
              Axumin PET/CT scanning.`}
            </Typography>
          </Collapse>
        </div>
        <div className={classes.charcoalGreyBox}>
          <div className={classes.headerRow}>
            <Typography
              className={classNames(
                classes.titleText,
                currentTraining && currentTraining.dateCompleted ? ' ' : classes.disabledText,
                isLCExpanded ? classes.expanded : '',
              )}
              onClick={LCExpandHandler}
            >
              Comprehensive Review of Learning Checks
            </Typography>
            <Button
              variant="contained"
              disabled={!currentTraining || !currentTraining.dateCompleted}
              color="primary"
              className={classes.enterButton}
              href={
                module === 'axuminInterpretation'
                  ? 'interpretation/learningcheckreview'
                  : 'acquisition/learningcheckreview'
              }
            >
              Enter
            </Button>
          </div>
          <Collapse in={isLCExpanded} timeout="auto" unmountOnExit>
            <Typography className={classes.h2}>Description:</Typography>
            <Typography className={classes.bodyText}>
              {`Upon conclusion of the Required Image ${displayModule} Training, you can review the correct answers for all Learning Check Questions.`}
            </Typography>
          </Collapse>
        </div>
        <div className={classes.charcoalGreyBox}>
          <div className={classes.headerRow}>
            <Typography
              className={classNames(
                classes.titleText,
                currentTraining && currentTraining.dateCompleted ? ' ' : classes.disabledText,
                isStatementExpanded ? classes.expanded : '',
              )}
              onClick={statementExpandHandler}
            >
              {`Image ${displayModule} Statement of Completion`}
            </Typography>
            <Button
              variant="contained"
              disabled={!currentTraining || !currentTraining.dateCompleted}
              color="primary"
              className={classes.enterButton}
              onClick={handleClickDownload}
              href={`${BASE_URL}${
                module === 'axuminInterpretation'
                  ? DOCUMENT_PATHS[1].INTERPRETATION_COMPLETE.path
                  : DOCUMENT_PATHS[1].ACQUISITION_COMPLETE.path
              }`}
              target="_blank"
              rel="noreferrer"
            >
              Download
            </Button>
          </div>
          <Collapse in={isStatementExpanded} timeout="auto" unmountOnExit>
            <Typography className={classes.h2}>Description:</Typography>
            <Typography className={classes.bodyText}>
              {`Upon conclusion of the Required Image ${displayModule} Training, you can download a statement of completion.`}
            </Typography>
          </Collapse>
        </div>
      </div>
      <Indication />
      <TrainingFooter />
    </>
  );
};

export default AxuminTrainingOverview;
